import {
  Bus,
  CalendarOff,
  ClipboardCheck,
  FileCheck2,
  Gauge,
  IdCard,
  Route,
  ShieldCheck,
  UserMinus,
  UserPlus,
  UserX,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import type {
  DriverSummary,
  RouteSummary,
  StudentTransportSummary,
  TrackingSummary,
  TransportSummary,
  VehicleSummary,
} from '@/types/transport';

/**
 * One figure above the section.
 *
 * Generic over what it reads, so the same row serves the dashboard's five
 * transport-wide figures and the Routes tab's four route ones — a prop rather
 * than a second row component (CLAUDE.md §3).
 */
export interface Tile<T> {
  key: string;
  label: string;
  icon: LucideIcon;
  value: (source: T) => string;
  hint: (source: T) => string;
  badgeStyle: string;
  cardStyle: string;
  titleStyle: string;
  numberStyle: string;
  hintStyle: string;
}

/** The five colour families the tiles cycle through, in the console's own set. */
const PAINT = {
  indigo: {
    badgeStyle: 'bg-indigo-600 text-white shadow-sm shadow-indigo-600/30',
    cardStyle:
      'bg-indigo-50/90 dark:bg-indigo-950/40 border-indigo-200/80 dark:border-indigo-800/60 hover:shadow-lg hover:shadow-indigo-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-indigo-900 dark:text-indigo-200',
    numberStyle: 'text-indigo-950 dark:text-white',
    hintStyle: 'text-indigo-700 dark:text-indigo-300',
  },
  sky: {
    badgeStyle: 'bg-sky-600 text-white shadow-sm shadow-sky-600/30',
    cardStyle:
      'bg-sky-50/90 dark:bg-sky-950/40 border-sky-200/80 dark:border-sky-800/60 hover:shadow-lg hover:shadow-sky-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-sky-900 dark:text-sky-200',
    numberStyle: 'text-sky-950 dark:text-white',
    hintStyle: 'text-sky-700 dark:text-sky-300',
  },
  violet: {
    badgeStyle: 'bg-violet-600 text-white shadow-sm shadow-violet-600/30',
    cardStyle:
      'bg-violet-50/90 dark:bg-violet-950/40 border-violet-200/80 dark:border-violet-800/60 hover:shadow-lg hover:shadow-violet-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-violet-900 dark:text-violet-200',
    numberStyle: 'text-violet-950 dark:text-white',
    hintStyle: 'text-violet-700 dark:text-violet-300',
  },
  amber: {
    badgeStyle: 'bg-amber-500 text-white shadow-sm shadow-amber-500/30',
    cardStyle:
      'bg-amber-50/90 dark:bg-amber-950/40 border-amber-200/80 dark:border-amber-800/60 hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-amber-900 dark:text-amber-200',
    numberStyle: 'text-amber-950 dark:text-white',
    hintStyle: 'text-amber-700 dark:text-amber-300',
  },
  emerald: {
    badgeStyle: 'bg-emerald-600 text-white shadow-sm shadow-emerald-600/30',
    cardStyle:
      'bg-emerald-50/90 dark:bg-emerald-950/40 border-emerald-200/80 dark:border-emerald-800/60 hover:shadow-lg hover:shadow-emerald-500/10 hover:-translate-y-0.5',
    titleStyle: 'text-emerald-900 dark:text-emerald-200',
    numberStyle: 'text-emerald-950 dark:text-white',
    hintStyle: 'text-emerald-700 dark:text-emerald-300',
  },
} as const;

/**
 * The five figures above the section on the dashboard.
 *
 * Drawn in the same families the roll's tiles use — indigo, sky, violet, amber,
 * emerald — rather than in a hue of transport's own: a reader moving from
 * Students to Transport should not feel they have changed product (CLAUDE.md
 * §7). The mock these were drawn from is a dark green console; the layout came
 * from it and the colours did not.
 */
export const TRANSPORT_TILES: Tile<TransportSummary>[] = [
  {
    key: 'vehicles',
    label: 'Total vehicles',
    icon: Bus,
    value: (s) => String(s.totalVehicles),
    hint: (s) => `${s.activeVehicles} active`,
    ...PAINT.indigo,
  },
  {
    key: 'routes',
    label: 'Total routes',
    icon: Route,
    value: (s) => String(s.totalRoutes),
    hint: (s) => `${s.activeRoutes} active`,
    ...PAINT.sky,
  },
  {
    key: 'students',
    label: 'Students transported',
    icon: Users,
    value: (s) => String(s.studentsTransported),
    hint: () => 'Today',
    ...PAINT.violet,
  },
  {
    key: 'alerts',
    label: 'Safety alerts',
    icon: ShieldCheck,
    value: (s) => String(s.safetyAlerts),
    hint: () => 'Today',
    ...PAINT.amber,
  },
  {
    key: 'onTime',
    label: 'On-time performance',
    icon: FileCheck2,
    value: (s) => `${s.onTimePerformance}%`,
    hint: () => 'This month',
    ...PAINT.emerald,
  },
];

/** What the Routes tab counts instead: the register rather than the day. */
export const ROUTE_TILES: Tile<RouteSummary>[] = [
  {
    key: 'routes',
    label: 'Total routes',
    icon: Route,
    value: (s) => String(s.totalRoutes),
    hint: (s) => `${s.activeRoutes} active`,
    ...PAINT.sky,
  },
  {
    key: 'distance',
    label: 'Total distance',
    icon: Gauge,
    value: (s) => `${s.totalDistanceKm} km`,
    hint: () => 'All routes',
    ...PAINT.emerald,
  },
  {
    key: 'vehicles',
    label: 'Total vehicles',
    icon: Bus,
    value: (s) => String(s.vehiclesAssigned),
    hint: () => 'Assigned',
    ...PAINT.violet,
  },
  {
    key: 'students',
    label: 'Students',
    icon: Users,
    value: (s) => String(s.studentsUsingRoutes),
    hint: () => 'Using routes',
    ...PAINT.amber,
  },
];

/**
 * What the Vehicles tab counts: where the fleet is, and whether it is legal.
 *
 * The three standings are shown as shares of the fleet as well as counts —
 * "2 under maintenance" means something different in a fleet of five than in a
 * fleet of fifty, and the tile is read at a glance by people who know their own
 * fleet size but not their arithmetic.
 */
export const VEHICLE_TILES: Tile<VehicleSummary>[] = [
  {
    key: 'total',
    label: 'Total vehicles',
    icon: Bus,
    value: (s) => String(s.totalVehicles),
    hint: (s) => `${s.activeVehicles} active`,
    ...PAINT.indigo,
  },
  {
    key: 'onRoute',
    label: 'On route',
    icon: ClipboardCheck,
    value: (s) => String(s.onRoute),
    hint: (s) => shareOf(s.onRoute, s.totalVehicles),
    ...PAINT.sky,
  },
  {
    key: 'maintenance',
    label: 'Under maintenance',
    icon: Wrench,
    value: (s) => String(s.underMaintenance),
    hint: (s) => shareOf(s.underMaintenance, s.totalVehicles),
    ...PAINT.amber,
  },
  {
    key: 'inactive',
    label: 'Inactive',
    icon: ShieldCheck,
    value: (s) => String(s.inactive),
    hint: (s) => shareOf(s.inactive, s.totalVehicles),
    ...PAINT.violet,
  },
  {
    key: 'fitness',
    label: 'Fitness compliance',
    icon: FileCheck2,
    value: (s) => `${s.fitnessCompliance}%`,
    hint: () => 'This month',
    ...PAINT.emerald,
  },
];

/** What the Drivers tab counts: where the roll is, and whether its licences hold. */
export const DRIVER_TILES: Tile<DriverSummary>[] = [
  {
    key: 'total',
    label: 'Total drivers',
    icon: UserPlus,
    value: (s) => String(s.totalDrivers),
    hint: (s) => `${s.activeDrivers} active`,
    ...PAINT.indigo,
  },
  {
    key: 'onDuty',
    label: 'On duty',
    icon: ClipboardCheck,
    value: (s) => String(s.onDuty),
    hint: (s) => shareOf(s.onDuty, s.totalDrivers),
    ...PAINT.sky,
  },
  {
    key: 'onLeave',
    label: 'On leave',
    icon: CalendarOff,
    value: (s) => String(s.onLeave),
    hint: (s) => shareOf(s.onLeave, s.totalDrivers),
    ...PAINT.amber,
  },
  {
    key: 'inactive',
    label: 'Inactive',
    icon: UserX,
    value: (s) => String(s.inactive),
    hint: (s) => shareOf(s.inactive, s.totalDrivers),
    ...PAINT.violet,
  },
  {
    key: 'licence',
    label: 'Licence valid',
    icon: IdCard,
    value: (s) => `${s.licenceValid}%`,
    hint: () => 'This month',
    ...PAINT.emerald,
  },
];

/** What the Students tab counts: who rides, who does not, and on how many routes. */
export const STUDENT_TILES: Tile<StudentTransportSummary>[] = [
  {
    key: 'total',
    label: 'Total students',
    icon: Users,
    value: (s) => String(s.totalStudents),
    hint: () => 'On the roll',
    ...PAINT.indigo,
  },
  {
    key: 'using',
    label: 'Using transport',
    icon: Bus,
    value: (s) => String(s.usingTransport),
    hint: (s) => shareOf(s.usingTransport, s.totalStudents),
    ...PAINT.sky,
  },
  {
    key: 'notUsing',
    label: 'Not using transport',
    icon: UserMinus,
    value: (s) => String(s.notUsingTransport),
    hint: (s) => shareOf(s.notUsingTransport, s.totalStudents),
    ...PAINT.violet,
  },
  {
    key: 'routes',
    label: 'Routes assigned',
    icon: Route,
    value: (s) => String(s.routesAssigned),
    hint: () => 'In use',
    ...PAINT.amber,
  },
  {
    key: 'compliance',
    label: 'Transport compliance',
    icon: ShieldCheck,
    value: (s) => `${s.compliance}%`,
    hint: () => 'This month',
    ...PAINT.emerald,
  },
];

/**
 * What the Tracking tab counts.
 *
 * The first four are the Students tab's own tiles, reading a different object —
 * the office watching the map is watching it on behalf of those children, and
 * two different sets of numbers across the two tabs would invite a reader to
 * work out which was right. Only the last tile is this tab's own.
 */
export const TRACKING_TILES: Tile<TrackingSummary>[] = [
  {
    key: 'total',
    label: 'Total students',
    icon: Users,
    value: (s) => String(s.totalStudents),
    hint: () => 'On the roll',
    ...PAINT.indigo,
  },
  {
    key: 'using',
    label: 'Using transport',
    icon: Bus,
    value: (s) => String(s.usingTransport),
    hint: (s) => shareOf(s.usingTransport, s.totalStudents),
    ...PAINT.sky,
  },
  {
    key: 'notUsing',
    label: 'Not using transport',
    icon: UserMinus,
    value: (s) => String(s.notUsingTransport),
    hint: (s) => shareOf(s.notUsingTransport, s.totalStudents),
    ...PAINT.violet,
  },
  {
    key: 'routes',
    label: 'Routes assigned',
    icon: Route,
    value: (s) => String(s.routesAssigned),
    hint: () => 'In use',
    ...PAINT.amber,
  },
  {
    key: 'distance',
    label: 'Total distance covered',
    icon: Gauge,
    value: (s) => `${s.distanceCoveredKm} km`,
    hint: () => 'Today',
    ...PAINT.emerald,
  },
];

/** `15` of `18` → `83.33%`. Two decimals, because a fleet of 18 divides badly. */
function shareOf(part: number, whole: number): string {
  return whole === 0 ? '—' : `${((part / whole) * 100).toFixed(2)}%`;
}

export interface TransportStatRowProps<T> {
  /** Which figures to draw — `TRANSPORT_TILES` or `ROUTE_TILES`. */
  tiles: Tile<T>[];
  /** Whatever those tiles read. */
  summary: T;
}

/**
 * The row of figures above the section.
 *
 * The column count follows the number of tiles rather than being fixed at five:
 * four tiles in a five-column grid leaves a gap at the end that reads as a tile
 * that failed to load.
 */
export function TransportStatRow<T>({ tiles, summary }: TransportStatRowProps<T>) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-full',
        tiles.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3 lg:grid-cols-5',
      )}
    >
      {tiles.map((tile, index) => {
        const Icon = tile.icon;
        return (
          <div
            key={tile.key}
            className={cn(
              'flex flex-col justify-between rounded-2xl sm:rounded-3xl border p-3 sm:p-4 shadow-md transition-all duration-200 animate-fade-up min-w-0 overflow-hidden',
              tile.cardStyle,
              tiles.length === 5 && index === 4 && 'col-span-2 sm:col-span-1',
            )}
            // Capped so a five-tile row finishes arriving before the reader has
            // finished looking at it.
            style={{ animationDelay: `${Math.min(index, 4) * 60}ms` }}
          >
            <div className="flex items-start justify-between gap-2">
              <p className={cn('text-xs sm:text-[14px] font-bold leading-tight line-clamp-1', tile.titleStyle)}>
                {tile.label}
              </p>
              <span
                className={cn('grid h-8 w-8 sm:h-9 sm:w-9 shrink-0 place-items-center rounded-xl shadow-xs', tile.badgeStyle)}
                aria-hidden
              >
                <Icon className="h-4 w-4 sm:h-4.5 sm:w-4.5" />
              </span>
            </div>

            <div className="mt-1 min-w-0">
              <p className={cn('text-2xl font-black mt-0.5 block leading-tight tracking-tight whitespace-nowrap tabular-nums truncate', tile.numberStyle)}>
                {tile.value(summary)}
              </p>
              <p className={cn('mt-0.5 sm:mt-1 truncate text-[11px] sm:text-xs font-bold', tile.hintStyle)}>
                {tile.hint(summary)}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
